import * as d3 from 'd3';
import {scaleLinear} from 'd3';
import _ from 'underscore';

import * as alignmentviewutil from 'analyses/alignment/AlignmentViewUtil';
import {Sequence} from 'components/Sequence';
import * as MapsConstants from 'constants/MapsConstants';
import CircularMapView from 'molbio/maps/circular/CircularMap';
import Slice from 'molbio/maps/linear/Slice';
import {SliceView} from 'molbio/maps/linear/SliceView';
import {
  AlignmentMapState,
  CircularMapState,
  MiniMapState,
  PolymerLinearMapState,
} from 'molbio/maps/MapStates';
import {ModifiedNucleotidesLegendView} from 'molbio/maps/options/ModifiedNucleotidesLegendView';
import Range from 'molbio/maps/selectables/Range';
import Type from 'molbio/maps/Type';
import Pagination from 'molbio/pdf/Pagination';
import * as sequtil from 'molbio/SeqUtil';
import CircularMapTabState from 'molbio/tabstates/CircularMapTabState';
import MiniMapTabState from 'molbio/tabstates/MiniMapTabState';
import SequenceAlignmentTabState from 'molbio/tabstates/SequenceAlignmentTabState';
import {GenericLinearMapTabState} from 'molbio/tabstates/SequenceLinearMapTabState';
import {fixAxis} from 'util/SVGUtil';

const INKSCAPE_SVG_SUPPORTS_FOREIGN_OBJECT = false;

function circularPaginate(
  tabstate: CircularMapTabState,
  mapstate: CircularMapState,
  css: string | null
): Pagination {
  const sequence = mapstate.get('sequence');

  const pagination = new Pagination({css});

  const title = `${sequence.get('_sequence').displayNames()} (${sequence.length()} bp)`;
  pagination.addTitle(title);
  const targetWidth = pagination.innerWidth();

  mapstate = new CircularMapState({
    width: targetWidth,
    height: targetWidth,
    visibilityOptions: mapstate.get('visibilityOptions'),
    fullCircleView: false,
    sequence: mapstate.get('sequence'),
  });

  const circularMapOptions = {model: mapstate, tabstate, renderSelection: false};
  const circularMapView = new CircularMapView(circularMapOptions);
  circularMapView.render();
  circularMapView.stopListening();
  pagination.addSVG(d3.select(circularMapView.el).select<SVGSVGElement | null>('svg').node()!, {
    start: 0,
    end: sequence.length(),
  });
  pagination.setHeaders(title, sequence.length(), sequence.startIndex());
  return pagination;
}

function minimapPaginate(tabstate: MiniMapTabState, mapstate: MiniMapState, css: string | null): Pagination {
  const sequence = mapstate.get('sequence');
  const pagination = new Pagination({
    css,
  });

  // 1. Render title
  const title = `${sequence.get('_sequence').displayNames()} (${sequence.length()} bp)`;
  pagination.addTitle(title);
  const targetWidth = pagination.innerWidth();

  // 2. Render map
  // Duplicate the existing mapstate, BUT we might need to recompute the layers
  // in the case we have are using inline labels. To prevent overwriting the
  // minimap's layering, we use a different name for the layer:
  //     inlineLayer-pdf instead of inlineLayer
  mapstate = new MiniMapState(
    {
      fontWidthZoom: 0,
      layout: mapstate.get('layout'),
      visibilityOptions: mapstate.get('visibilityOptions'),
      sequence: mapstate.get('sequence'),
      leftBP: 0,
    },
    {
      inlineLayeringMode: MapsConstants.LINEAR_LAYER_INLINE_PDF,
    }
  );

  const [start, end] = [0, sequence.length()];
  const x = scaleLinear().clamp(true).domain([start, end]).range([0, targetWidth]);

  const xInv = scaleLinear().clamp(true).domain([0, targetWidth]).range([start, end]);

  const slice = new Slice({
    start,
    end,
    leftBP: start,
    rightBP: end,
    x,
    xInv,
    width: targetWidth,
    maxWidth: targetWidth,
    visibleWidth: targetWidth,
  });
  sequtil.setCutsiteData(sequence, [slice], sequence.length());

  if (MapsConstants.LINEAR_INLINE_LAYERS.includes(mapstate.get('layeringMode'))) {
    mapstate.setInlineLayers(slice);
  }
  const sliceView = new SliceView({
    slice,
    selection: sequence.selection(),
    model: mapstate,
    tabstate,
    mapType: MapsConstants.SliceMapType.minimap,
    y: MapsConstants.MINI_MAP_Y_COMPONENTS.filter((name) => name !== MapsConstants.SliceComponentName.title),
    z: MapsConstants.MINI_MAP_Z_COMPONENTS.filter((name) => name !== MapsConstants.SliceComponentName.title),
    svgSupportsForeignObject: INKSCAPE_SVG_SUPPORTS_FOREIGN_OBJECT,
  });
  sliceView.render();
  sliceView.killSelection();
  sliceView.stopListening();
  const svg = sliceView.el;
  d3.select(svg).classed('mini-map', true);
  fixAxis(svg);
  pagination.addSVG(svg, slice.pick('start', 'end'));

  // 3. Render headers
  pagination.setHeaders(title, sequence.length(), sequence.startIndex());
  return pagination;
}

function linearPaginate(
  tabstate: GenericLinearMapTabState,
  mapstate: PolymerLinearMapState<Sequence>,
  css: string | null,
  renderModifiedNucleotideLegend?: () => ModifiedNucleotidesLegendView
): Pagination {
  const sequence = mapstate.get('sequence');
  const layout = mapstate.get('layout');
  const type = layout.DNA.get('type');

  const pagination = new Pagination({css});

  const title = `${sequence.get('_sequence').displayNames()} (${sequence.length()} bp)`;
  pagination.addTitle(title);
  const targetWidth = pagination.innerWidth();

  if (renderModifiedNucleotideLegend != null) {
    const legend = renderModifiedNucleotideLegend();
    pagination.addSVG(legend.el.cloneNode(true), null);
  }

  const basesPerSlice = type.numChars(targetWidth);

  const slices = [];
  for (let start = 0; start < sequence.length(); start += basesPerSlice) {
    const end = Math.min(start + basesPerSlice, sequence.length());
    const width = type.width(end - start) + type.letterSpacing;
    const x = scaleLinear().clamp(true).domain([start, end]).range([0, width]);
    const xInv = scaleLinear().clamp(true).domain([0, width]).range([start, end]);
    slices.push(
      new Slice({
        start,
        end,
        width,
        x,
        xInv,
        maxWidth: targetWidth,
        visibleWidth: targetWidth,
      })
    );
  }
  sequtil.setCutsiteData(sequence, slices, basesPerSlice);

  for (const slice of slices) {
    let y = MapsConstants.LINEAR_MAP_Y_COMPONENTS;
    let z = MapsConstants.LINEAR_MAP_Z_COMPONENTS;
    if (sequence.isOligo()) {
      y = MapsConstants.OLIGO_MAP_Y_COMPONENTS;
      z = MapsConstants.OLIGO_MAP_Z_COMPONENTS;
    } else if (sequence.isRNASequence()) {
      y = MapsConstants.RNA_LINEAR_MAP_Y_COMPONENTS;
      z = MapsConstants.RNA_LINEAR_MAP_Z_COMPONENTS;
    }

    const sliceView = new SliceView({
      slice,
      model: mapstate,
      selection: sequence.selection(),
      tabstate,
      mapType: MapsConstants.SliceMapType.linear,
      y,
      z,
      svgSupportsForeignObject: INKSCAPE_SVG_SUPPORTS_FOREIGN_OBJECT,
    });
    sliceView.render();
    sliceView.killSelection();
    sliceView.stopListening();
    const svg = sliceView.el;
    fixAxis(svg);
    const hadSpace = pagination.addSVG(svg, slice.pick('start', 'end'));
    if (!hadSpace) {
      break;
    }
  }
  pagination.setHeaders(title, sequence.length(), sequence.startIndex());
  return pagination;
}

function alignmentPaginate(tabstate: SequenceAlignmentTabState, css: string | null): Pagination {
  let end;
  let start;
  const sequence = tabstate.get('mapstates').models[0].get('sequence');
  const type = tabstate.get('dnaType');
  const analysis = tabstate.get('analysis')!;

  const pagination = new Pagination({landscape: true, css});
  const titledType = analysis.get('alignmentType').replace(/^[a-z]/g, (chr: string) => chr.toUpperCase());
  const title = `${titledType} Alignment: ${analysis.name()}`;
  pagination.addTitle(title);
  const targetWidth = pagination.innerWidth();

  const basesPerSlice = type.numChars(targetWidth);
  const slices = (() => {
    const result = [];

    const selLength = sequence.selection().length();
    const selStart = selLength ? sequence.selection().start() : 0;
    // Compute end using selection.length() to avoid edge case where end=0
    const selEnd = selLength ? selStart + selLength : sequence.length();

    for (start = selStart; start < selEnd; start += basesPerSlice) {
      end = Math.min(start + basesPerSlice, selEnd);
      const width = type.width(end - start) + type.letterSpacing;
      const x = scaleLinear().clamp(true).domain([start, end]).range([0, width]);
      const xInv = scaleLinear().clamp(true).domain([0, width]).range([start, end]);
      result.push(
        new Slice({
          start,
          end,
          width,
          x,
          xInv,
          maxWidth: targetWidth,
          visibleWidth: targetWidth,
        })
      );
    }
    return result;
  })();

  const alignmentMapstates = _.sortBy(tabstate.get('mapstates').models, (mapstate: AlignmentMapState, i) => {
    if (mapstate.get('alignment').get('type') === analysis.get('alignmentType')) {
      return -1;
    } else {
      return i;
    }
  });

  for (const slice of slices) {
    const svgs = [];
    const range = new Range({
      getLengthFn() {
        // This length is used to cap how zoomed out the viewport can get, which does not affect the export
        return sequence.length();
      },
      start: slice.start(),
      end: slice.end(),
    });
    for (let i = 0; i < alignmentMapstates.length; i++) {
      const alignmentMapState = alignmentMapstates[i];
      const sliceView = alignmentviewutil.makeSliceView(
        slice,
        alignmentMapState,
        tabstate,
        range,
        INKSCAPE_SVG_SUPPORTS_FOREIGN_OBJECT
      );
      sliceView.render();
      const boxHeight = sliceView.boxHeight();
      sliceView.killSelection();
      sliceView.stopListening();
      const svg = sliceView.el;

      const nameType = new Type(12);
      const sequenceName = d3
        .select(svg)
        .append('svg:g')
        .attr('transform', `translate(0,${boxHeight})`)
        .append('svg:text')
        .attr('font-size', nameType.fontSize)
        .attr('dy', nameType.height())
        .attr('x', 20);
      sequenceName
        .append('svg:tspan')
        .attr('font-weight', 900)
        .text(`${alignmentMapState.get('alignment').get('type')} sequence`);
      sequenceName
        .append('svg:tspan')
        .attr('dx', nameType.charWidth())
        .text(`${alignmentMapState.get('sequence').name()}`);

      d3.select(svg)
        .attr('height', boxHeight + nameType.height())
        .classed('alignment', true);
      fixAxis(svg);
      svgs.push(svg);
    }
    pagination.addSVGGroup(svgs, slice.pick('start', 'end'));
  }
  pagination.setHeaders(title, sequence.length(), sequence.startIndex());
  return pagination;
}

export {circularPaginate, minimapPaginate, linearPaginate, alignmentPaginate};
